import { Injectable } from '@angular/core';
import { UserQuizData } from '../features/quiz/quiz.model';

export interface PendingPayment {
  payment_id?: string;
  zarinpal_authority?: string;
  timestamp: number;
  userQuizData: UserQuizData | null;
  service: any;
  selectedDuration: any;
  appliedCoupon: any;
  totalCost: number;
  status: 'pending' | 'verifying' | 'completed' | 'failed' | 'cancelled';
}

export interface QuizProgress {
  currentQuestionState: string;
  currentStepIndex: number;
  userData: UserQuizData;
}

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  private readonly userDataKey = 'userData';
  private readonly pendingPaymentKey = 'pendingPayment';
  private readonly quizProgressKey = 'quizProgress';

  constructor() { }

  getDataByKey(key: string): string | null {
    return localStorage.getItem(key);
  }

  setDataByKey(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  getUserData(): UserQuizData | null {
    try {
      const userDataString = localStorage.getItem(this.userDataKey);
      if (userDataString) {
        return JSON.parse(userDataString);
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      // Clear corrupted data
      this.clearUserData();
    }
    return null;
  }

  setUserData(userData: UserQuizData): void {
    try {
      const userDataString = JSON.stringify(userData);
      localStorage.setItem(this.userDataKey, userDataString);
    } catch (error) {
      console.error('Error saving user data to localStorage:', error);
    }
  }

  clearUserData(): void {
    localStorage.removeItem(this.userDataKey);
  }

  // Quiz progress methods
  getQuizProgress(): QuizProgress | null {
    try {
      const progressString = localStorage.getItem(this.quizProgressKey);
      if (progressString) {
        return JSON.parse(progressString);
      }
    } catch (error) {
      console.error('Error parsing quiz progress from localStorage:', error);
      this.clearQuizProgress();
    }
    return null;
  }

  setQuizProgress(progress: QuizProgress): void {
    try {
      const progressString = JSON.stringify(progress);
      localStorage.setItem(this.quizProgressKey, progressString);
    } catch (error) {
      console.error('Error saving quiz progress to localStorage:', error);
    }
  }

  clearQuizProgress(): void {
    localStorage.removeItem(this.quizProgressKey);
  }

  // Check if quiz is completed
  isQuizCompleted(): boolean {
    const userData = this.getUserData();
    const progress = this.getQuizProgress();

    // Quiz is completed if we have user data but no progress
    return userData !== null && progress === null;
  }

  // Check if quiz is in progress
  isQuizInProgress(): boolean {
    const progress = this.getQuizProgress();
    return progress !== null;
  }

  // Payment tracking methods
  setPendingPayment(payment: PendingPayment): void {
    try {
      const paymentString = JSON.stringify(payment);
      localStorage.setItem(this.pendingPaymentKey, paymentString);
    } catch (error) {
      console.error('Error saving pending payment to localStorage:', error);
    }
  }

  getPendingPayment(): PendingPayment | null {
    try {
      const paymentData = localStorage.getItem(this.pendingPaymentKey);
      return paymentData ? JSON.parse(paymentData) : null;
    } catch (error) {
      console.error('Error parsing pending payment from localStorage:', error);
      this.clearPendingPayment();
      return null;
    }
  }

  updatePendingPaymentStatus(
    status: 'pending' | 'verifying' | 'completed' | 'failed' | 'cancelled',
    payment_id?: string,
    zarinpal_authority?: string
  ): void {
    const payment = this.getPendingPayment();
    if (payment) {
      payment.status = status;
      if (payment_id) {
        payment.payment_id = payment_id;
      }
      if (zarinpal_authority) {
        payment.zarinpal_authority = zarinpal_authority;
      }
      this.setPendingPayment(payment);
    }
  }

  clearPendingPayment(): void {
    localStorage.removeItem(this.pendingPaymentKey);
  }

  hasPendingPayment(): boolean {
    const payment = this.getPendingPayment();
    return payment !== null && payment.status !== 'completed';
  }

  // Clear all data (useful for logout or reset)
  clearAllData(): void {
    this.clearUserData();
    this.clearQuizProgress();
    this.clearPendingPayment();
  }
}
